import React from 'react';
import { Star } from '@material-ui/icons';
import { Typography } from '@material-ui/core';
import { Trans } from '@lingui/macro';
import { FeatureFlagsEnum, SiteSummaryModel } from '@shared/models';
import { useSelectedSitesDataSource, useSitesDataSource } from '../sitesDataSource';
import { useSidebar } from '@shared/hooks';
import { useSiteListState } from '../useSiteListFiltersState';
import { isNil } from 'lodash';
import { useFeatureFlag, useFormatDate } from '@hwm/ui-lib';

export interface SiteListItemProps {
  siteData: SiteSummaryModel;
  height: number;
  dataTestId: string;
}

export const SiteListItem: React.FC<SiteListItemProps> = ({ siteData, height, dataTestId }) => {
  const { hasFeatureFlag } = useFeatureFlag();
  const canSeeECLData = hasFeatureFlag(FeatureFlagsEnum.ECLData);

  const selected = siteData?.isSelected;
  const { device } = siteData ?? {};

  const { handleSidebarState } = useSidebar();

  const { formatDate } = useFormatDate();

  const { handleFavorites, setGraphSites } = useSitesDataSource();
  const { selectedSitesList, handleLoadSelectedList } = useSelectedSitesDataSource();

  const { setSiteFilter } = useSiteListState();

  return siteData ? (
    <div
      style={{ height: '100%', minHeight: height }}
      className="flex flex-col mb-2"
      data-testid={dataTestId}
      data-sitename={siteData.siteID}
    >
      <div className="grid grid-cols-12 gap-1 sm:gap-2 items-center mb-1">
        <div className="flex flex-row items-center h-7 col-span-12 xl:col-span-4">
          <div
            className="flex cursor-pointer mr-2"
            onClick={() => handleFavorites(!selected, siteData?.id, false, setSiteFilter)}
            data-testid={`${dataTestId}-favorite-button`}
          >
            {selected ? (
              <Star style={{ fill: 'gold', stroke: 'black' }} />
            ) : (
              <Star style={{ fill: 'transparent', stroke: 'black' }} />
            )}
          </div>
          <div
            className="flex justify-center items-center h-full bg-primaryColor px-2 py-1 md:py-2 text-white rounded-lg cursor-pointer transition hover:opacity-80"
            style={{ width: 'calc(100% - 32px)', minWidth: 'calc(100% - 32px)' }}
            onClick={() => {
              const addToFav = () =>
                handleFavorites(true, siteData?.id, selected, null, handleSidebarState, () => setGraphSites([]));
              handleLoadSelectedList(null, addToFav, !selectedSitesList);
            }}
            data-testid={`${dataTestId}-name`}
          >
            <Typography style={{ fontWeight: 500 }} variant="body2" noWrap>
              {siteData?.siteID}
            </Typography>
          </div>
        </div>
        <div
          className="flex items-center h-7 bg-grey2 px-2 py-1 md:py-2 rounded-lg col-span-12 xl:col-span-8"
          data-testid={`${dataTestId}-address`}
        >
          <Typography variant="body2" noWrap>
            {siteData?.address ?? '-'}
          </Typography>
        </div>
      </div>
      <div className="flex flex-row flex-wrap">
        <Typography
          className="md:w-44"
          variant="body2"
          style={{ marginRight: 8 }}
          data-testid={`${dataTestId}-lastcall`}
        >
          <b>
            <Trans>Call In</Trans>
          </b>
          : {device?.lastCallIn ? formatDate(device?.lastCallIn, 'P HH:mm') : '-'}
        </Typography>
        <Typography className="md:w-40" variant="body2" style={{ marginRight: 8 }} data-testid={`${dataTestId}-serial`}>
          <b>
            <Trans>Serial</Trans>
          </b>
          : {device?.serialNumber ?? '-'}
        </Typography>
        <Typography className="md:w-44" variant="body2" style={{ marginRight: 8 }} data-testid={`${dataTestId}-sms`}>
          <b>
            <Trans>SMS</Trans>
          </b>
          : {device?.smsNumber ?? '-'}
        </Typography>
        <Typography
          variant="body2"
          style={{
            marginRight: 8,
          }}
          data-testid={`${dataTestId}-signal`}
        >
          <b>
            <Trans>Signal</Trans> -{' '}
            {[
              'CSQ',
              ...(canSeeECLData && !isNil(device?.ecl) ? ['ECL'] : []),
              ...(!isNil(device?.rsrp) ? ['RSRP'] : []),
              ...(!isNil(device?.rsrq) ? ['RSRQ'] : []),
            ].join(', ')}
          </b>
          :{' '}
          <>
            <span className="w-9">{device?.csq}</span>
            {canSeeECLData && !isNil(device?.ecl) && <span className="w-9">, {device.ecl}</span>}
            {!isNil(device?.rsrp) && <span className="w-9">, {device.rsrp}</span>}
            {!isNil(device?.rsrq) && <span className="w-9">, {device.rsrq}</span>}
          </>
        </Typography>
        <Typography
          className="md:w-36"
          variant="body2"
          style={{ marginRight: 8 }}
          data-testid={`${dataTestId}-battery`}
        >
          <b>
            <Trans>Battery Level</Trans>
          </b>
          : {device?.battery ?? '-'}
        </Typography>
        <Typography variant="body2" style={{ marginRight: 8 }} data-testid={`${dataTestId}-deviceType`}>
          <b>
            <Trans>Device Type</Trans>
          </b>
          : {device?.model ?? '-'}
        </Typography>
        <Typography variant="body2" style={{ marginRight: 8 }} data-testid={`${dataTestId}-fw`}>
          <b>
            <Trans>FW</Trans>
          </b>
          : {device?.version ?? '-'}
        </Typography>
        <Typography
          className="md:w-52"
          variant="body2"
          style={{ marginRight: 8 }}
          data-testid={`${dataTestId}-lastRestart`}
        >
          <b>
            <Trans>Last Restart</Trans>
          </b>
          : {device?.lastRestart ? formatDate(device?.lastRestart, 'P HH:mm') : '-'}
        </Typography>
      </div>
    </div>
  ) : null;
};
